import classNames from "classnames";
import React, { useRef, useState } from "react";
import { useScrollDirection } from "../../Pages/Events/EventCalendar/hooks/useScrollDirection";

export interface TabDefinition {
    id: string;
    label: string;
    content: React.ReactNode;
}

interface TabsProps {
    tabs: TabDefinition[];
    activeTabId: string;
    onTabChange?: (id: string) => void;
}

export const Tabs: React.FC<TabsProps> = ({ tabs, activeTabId, onTabChange }) => {
    const [focusedTabId, setFocusedTabId] = useState(activeTabId);
    const { isNavigationSticky, isNavigationHidden } = useScrollDirection();

    const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

    const focusTab = (id: string) => {
        tabRefs.current[id]?.focus();
        setFocusedTabId(id);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        const currentIndex = tabs.findIndex((t) => t.id === focusedTabId);
        let nextIndex = currentIndex;

        if (e.key === "ArrowRight") {
            nextIndex = (currentIndex + 1) % tabs.length;
        } else if (e.key === "ArrowLeft") {
            nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        } else if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onTabChange?.(focusedTabId);
            return;
        }

        const nextTabId = tabs[nextIndex].id;
        focusTab(nextTabId);
    };

    const handleClick = (id: string) => {
        setFocusedTabId(id);
        onTabChange?.(id);
    };

    return (
        <div className="Tabs">
            <div
                role="tablist"
                aria-label="Tab options"
                onKeyDown={handleKeyDown}
                className={classNames("Tabs__list", {
                    "nav-sticky": isNavigationSticky,
                    "nav-hidden": isNavigationHidden,
                })}
            >
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        id={`tab-${tab.id}`}
                        role="tab"
                        aria-selected={activeTabId === tab.id}
                        aria-controls={`tabpanel-${tab.id}`}
                        tabIndex={focusedTabId === tab.id ? 0 : -1}
                        ref={(el) => (tabRefs.current[tab.id] = el)}
                        onClick={() => handleClick(tab.id)}
                        className={classNames("Tabs__tab", {
                            "Tabs__tab--is-active": activeTabId === tab.id,
                        })}
                    >
                        <div>{tab.label}</div>
                    </button>
                ))}
            </div>

            {tabs.map((tab) =>
                activeTabId === tab.id ? (
                    <div
                        key={tab.id}
                        id={`tabpanel-${tab.id}`}
                        role="tabpanel"
                        aria-labelledby={`tab-${tab.id}`}
                        className="Tabs__panel"
                    >
                        {tab.content}
                    </div>
                ) : null
            )}
        </div>
    );
};
